import { Box, HStack, Icon, Spacer, Td, Text, Tooltip, Tr, VStack } from "@chakra-ui/react"
import { MarketListIcon } from "component/MarketListIcon"
import { Stats } from "component/Stats"
import { ICollateralStr } from "module/vault/hook/useCollateralStrings"
import { RiInformationLine } from "react-icons/ri"
import { CollateralActions } from "./CollateralActions"

interface CollateralRowProps {
    data: ICollateralStr
    ethAmount: string
    currentTableBreakpoint: string
    isWalletBalanceHidden: boolean
    isUsdcNegative: boolean
}

export const CollateralRow = ({
    data,
    ethAmount,
    currentTableBreakpoint,
    isWalletBalanceHidden,
    isUsdcNegative,
}: CollateralRowProps) => {
    const {
        symbol,
        name,
        vaultBalanceStr,
        vaultBalanceUsdStr,
        vaultBalanceUsdWeightedStr,
        walletBalanceStr,
        weight,
        freeCollateralStr,
    } = data
    // make WETH a special case
    // handle USDC info
    const isUsdc = symbol === "USDC"
    const isWETH = symbol === "WETH"
    const nameDisplayed = isWETH ? "Ether & Wrapped Ether" : isUsdc ? "Bridged USDC" : name
    const symbolDisplayed = isWETH ? "ETH & WETH" : isUsdc ? "USDC.E" : symbol

    return (
        <Tr>
            <Td>
                <HStack spacing={0}>
                    <Box
                        mr={currentTableBreakpoint === "sm" ? 2 : 3}
                        minW={currentTableBreakpoint === "sm" ? "18px" : "28px"}
                    >
                        <MarketListIcon boxSize={currentTableBreakpoint === "sm" ? "18px" : "28px"} coin={symbol} />
                    </Box>
                    <Stats
                        label={nameDisplayed}
                        number={symbolDisplayed}
                        variant="bottom"
                        numberProps={{ lineHeight: 1.3, fontWeight: "normal", textTransform: "uppercase" }}
                        labelProps={{ lineHeight: 1.2, textTransform: "capitalize" }}
                    />
                    {isUsdc && (
                        <>
                            <Spacer />
                            <Tooltip
                                label="The settlement token (USDC) reflects both realized (price, fee, funding) and unrealized PnL."
                                hasArrow
                            >
                                <Box color="gray.400" as="span">
                                    <Icon
                                        position="relative"
                                        top="4px"
                                        ml={"4px"}
                                        opacity="1"
                                        boxSize="16px"
                                        as={RiInformationLine}
                                    />
                                </Box>
                            </Tooltip>
                        </>
                    )}
                </HStack>
            </Td>
            <Td hidden={isWalletBalanceHidden} display={currentTableBreakpoint === "sm" ? "none" : undefined}>
                <VStack spacing={0} w="100%" align="left">
                    {isWETH ? (
                        <>
                            <Stats
                                numberComponent={
                                    <Text lineHeight={1.3} fontWeight="normal">
                                        {ethAmount}
                                        <Text as="span" color="gray.400" variant="label">
                                            {" "}
                                            ETH
                                        </Text>
                                    </Text>
                                }
                                variant="bottom"
                                numberProps={{ lineHeight: 1.3, fontWeight: "normal" }}
                            />
                            <Stats
                                numberComponent={
                                    <Text lineHeight={1.3} fontWeight="normal">
                                        {walletBalanceStr}
                                        <Text as="span" color="gray.400" variant="label">
                                            {" "}
                                            WETH
                                        </Text>
                                    </Text>
                                }
                                variant="right"
                                numberProps={{ lineHeight: 1.3, fontWeight: "normal" }}
                            />
                        </>
                    ) : (
                        <Stats
                            number={walletBalanceStr}
                            variant="bottom"
                            numberProps={{ lineHeight: 1.3, fontWeight: "normal" }}
                        />
                    )}
                </VStack>
            </Td>
            <Td>
                <HStack>
                    <Stats
                        label={vaultBalanceUsdWeightedStr || "0"}
                        number={vaultBalanceStr}
                        variant="bottom"
                        numberProps={{ lineHeight: 1.3, fontWeight: "normal" }}
                        labelProps={{ lineHeight: 1.2 }}
                    />
                    <Spacer />
                    {currentTableBreakpoint !== "sm" && (
                        <Tooltip
                            hasArrow
                            label={`Collateral Value = USD Value (${vaultBalanceUsdStr}) × Collateral Weight (${weight})`}
                        >
                            <Box>
                                <Box color="gray.400" as="span">
                                    <Icon
                                        position="relative"
                                        top="4px"
                                        ml={"4px"}
                                        opacity="1"
                                        boxSize="16px"
                                        as={RiInformationLine}
                                    />
                                </Box>
                            </Box>
                        </Tooltip>
                    )}
                </HStack>
            </Td>
            <Td
                display={currentTableBreakpoint === "sm" ? "none" : undefined}
                isNumeric={currentTableBreakpoint === "sm"}
            >
                <Stats
                    alignItems={currentTableBreakpoint === "sm" ? "flex-end" : "flex-start"}
                    // label={freeCollateralUsdStr ? formatUSD(freeCollateralUsdStr) : `$${AMOUNT_PLACEHOLDER}`}
                    number={freeCollateralStr}
                    variant="bottom"
                    numberProps={{
                        lineHeight: 1.3,
                        fontWeight: "normal",
                    }}
                    labelProps={{ lineHeight: 1.2 }}
                />
            </Td>
            <Td isNumeric>
                <CollateralActions
                    data={data}
                    isDisabled={isUsdcNegative}
                    isCollapsed={currentTableBreakpoint === "sm"}
                />
            </Td>
        </Tr>
    )
}
